// client/src/components/DateTimeSelect.jsx
/**
 * =================================================================
 * DateTimeSelect Component
 * =================================================================
 * Combined date + time picker that matches the date/time selection
 * used in the Task component (TaskAdd.jsx): a native <input type="date">
 * paired with the compact <TimeSelect> hour/minute/AM-PM dropdowns.
 *
 * Drop-in replacement for <input type="datetime-local">. It reads and
 * writes a single "YYYY-MM-DDTHH:MM" string so existing backend
 * conversion helpers keep working unchanged.
 *
 * When no time is set yet (e.g. a brand-new event action), the time
 * defaults to 5:00 PM instead of midnight.
 * =================================================================
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import TimeSelect from './TimeSelect';

// Module-level cache so multiple pickers on a page don't each refetch.
const tzCache = {};

const DateTimeSelect = ({
  value = '',
  onChange,
  tenantId,
  tenantTimezone: tenantTimezoneProp,
  showTimezone = true,
  disabled = false,
  id = 'datetime-select',
  defaultTime = '17:00',
}) => {
  const [tenantTimezone, setTenantTimezone] = useState(
    tenantTimezoneProp || 'America/Chicago'
  );

  useEffect(() => {
    if (tenantTimezoneProp) {
      setTenantTimezone(tenantTimezoneProp);
      return;
    }
    if (!tenantId) return;
    if (tzCache[tenantId]) {
      setTenantTimezone(tzCache[tenantId]);
      return;
    }
    const baseUrl = process.env.REACT_APP_BASE_URL || '';
    axios
      .get(`${baseUrl}/api/tenants/${tenantId}/timezone`)
      .then((res) => {
        const tz = res.data?.timezone;
        if (tz) {
          tzCache[tenantId] = tz;
          setTenantTimezone(tz);
        }
      })
      .catch(() => {});
  }, [tenantId, tenantTimezoneProp]);

  // Split the combined "YYYY-MM-DDTHH:MM" value into its parts.
  const hasT = typeof value === 'string' && value.includes('T');
  const datePart = hasT ? value.split('T')[0] : value || '';
  const rawTime = hasT ? value.split('T')[1] : '';
  const timePart = rawTime ? rawTime.slice(0, 5) : defaultTime;

  const emit = (newDate, newTime) => {
    if (!newDate) {
      onChange?.('');
      return;
    }
    onChange?.(`${newDate}T${newTime || defaultTime}`);
  };

  return (
    <div className="row">
      <div className="col-md-6 mb-2">
        <input
          type="date"
          className="form-control"
          value={datePart}
          onChange={(e) => emit(e.target.value, timePart)}
          disabled={disabled}
          id={`${id}-date`}
        />
      </div>
      <div className="col-md-6">
        <TimeSelect
          value={timePart}
          onChange={(t) => emit(datePart, t)}
          tenantTimezone={tenantTimezone}
          showTimezone={showTimezone}
          disabled={disabled}
          id={`${id}-time`}
        />
      </div>
    </div>
  );
};

export default DateTimeSelect;
